/**
 * React Query keys for source-ui.
 *
 * invalidateQueries vs refetch: Use invalidateQueries when data is updated from
 * another component or callback (e.g. after mutations, campaign refetch). Use
 * refetch when refreshing only in the same component (e.g. "Refresh" or "on open").
 *
 * isLoading vs isFetching: Use isLoading for initial load states (skeletons,
 * full-page loaders, disabled inputs) so the UI only shows loading when there
 * is no cached data. Use isFetching for background refresh indicators (e.g.
 * "loading more" in pagination) so you don't flash skeletons on every refetch.
 */
export const queryKeys = {
  auth: {
    whoami: ['auth', 'whoami'],
  },
  dashboard: {
    openings: ['dashboard', 'openings'],
    snapshots: ['dashboard', 'snapshots'],
    analytics: ['dashboard', 'analytics'],
    hiringOpenings: ['dashboard', 'hiringOpenings'],
    aggregateSpend: ['dashboard', 'aggregateSpend'],
    recommendations: {
      sourcing: ['dashboard', 'recommendations', 'sourcing'],
      automation: ['dashboard', 'recommendations', 'automation'],
      atRiskCount: ['dashboard', 'recommendations', 'atRiskCount'],
      atRiskOpenings: ['dashboard', 'recommendations', 'atRiskOpenings'],
    },
  },
  allOpenings: ['allOpenings'],
  openingDetails: {
    detail: ['openingDetails', 'detail'],
    hiringGoals: ['openingDetails', 'hiringGoals'],
    hiringOpenings: ['openingDetails', 'hiringOpenings'],
    channels: {
      list: ['openingDetails', 'channels', 'list'],
      sourcingFunnels: ['openingDetails', 'channels', 'sourcingFunnels'],
      campaignDetails: ['openingDetails', 'channels', 'campaignDetails'],
      generalRecommendation: [
        'openingDetails',
        'channels',
        'generalRecommendation',
      ],
      budgetRecommendation: [
        'openingDetails',
        'channels',
        'budgetRecommendation',
      ],
      recommendations: ['openingDetails', 'channels', 'recommendations'],
      reach: ['openingDetails', 'channels', 'reach'],
    },
    funnel: {
      details: ['openingDetails', 'funnel', 'details'],
      hireStages: ['openingDetails', 'funnel', 'hireStages'],
    },
  },
  editTargets: {
    suggestedBudget: ['editTargets', 'suggestedBudget'],
  },
  settings: {
    automations: ['settings', 'automations'],
    channels: {
      nonIntegrated: ['settings', 'channels', 'nonIntegrated'],
      utmMapping: ['settings', 'channels', 'utmMapping'],
      integrated: ['settings', 'channels', 'integrated'],
    },
    contracts: {
      vonqContracts: ['settings', 'contracts', 'vonqContracts'],
      vonqChannels: ['settings', 'contracts', 'vonqChannels'],
      vonqChannelDetails: ['settings', 'contracts', 'vonqChannelDetails'],
    },
  },
  jobPosting: {
    channels: {
      popular: ['jobPosting', 'channels', 'popular'],
      free: ['jobPosting', 'channels', 'free'],
      paid: ['jobPosting', 'channels', 'paid'],
    },
    jobDetails: ['jobPosting', 'jobDetails'],
    postingRequirements: ['jobPosting', 'postingRequirements'],
    audienceOptions: ['jobPosting', 'audienceOptions'],
    poolConflicts: ['jobPosting', 'poolConflicts'],
    vonqAutocomplete: {
      lazy: (contractId: string, fieldName: string) =>
        [
          'jobPosting',
          'vonqAutocomplete',
          contractId,
          fieldName,
          'lazy',
        ] as const,
      search: (contractId: string, fieldName: string, term: string) =>
        [
          'jobPosting',
          'vonqAutocomplete',
          contractId,
          fieldName,
          term,
        ] as const,
      dependent: (
        contractId: string,
        fieldName: string,
        term: string | string[],
      ) =>
        [
          'jobPosting',
          'vonqAutocomplete',
          contractId,
          fieldName,
          'dependent',
          JSON.stringify(term),
        ] as const,
    },
  },
} as const;
